import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplacePost.graphql';
import { Query, QuerymarketplacePostArgs } from '../../__generated__/globalTypes';

export const useMarketplacePostQuery = (options?: QueryHookOptions<Query, QuerymarketplacePostArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplacePostArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplacePostLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplacePostArgs>
): QueryResult<Query, QuerymarketplacePostArgs> & {
  getMarketplacePost: LazyQueryExecFunction<Query, QuerymarketplacePostArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplacePost, result] = useLazyQuery<Query, QuerymarketplacePostArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getMarketplacePost, ...result };
};

export const useMarketplacePostPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplacePost = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplacePostArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplacePostArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplacePost };
};
// Suspense Query Hooks
export const useMarketplacePostSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplacePostArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplacePostArgs>(QUERY, {
    ...options
  });

  return result;
};
